import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Collapse, Empty, Input, Modal, Popconfirm, Segmented, Select, Space, Spin, Table, Tag, Tooltip,
  Upload, message,
} from "antd";
import { CloudUploadOutlined, FileSearchOutlined, ReloadOutlined } from "@ant-design/icons";

import * as api from "./controller";
import {
  STATE_COLOURS, blankState, choiceValues, drawPlacement, factCollected, factLabel, factOptions, formatsFor,
  sampleValues,
} from "./breakdownFormatReview";
import { fetchProtectedImage } from "../TreatmentPlans/component/Breakdown/print/customFormat/officeFormat";
import { paintPages } from "../TreatmentPlans/component/Breakdown/print/customFormat/paintFormat";
import { renderFormatText } from "../TreatmentPlans/component/Breakdown/print/customFormat/formatText";
import { optionGroups } from "../TreatmentPlans/component/Breakdown/print/customFormat/layoutFormat";
import "./breakdownFormat.css";

/**
 * Office drawer → Breakdown format: the office's own paper breakdown sheet.
 *
 * Upload a copy (photo, scan or PDF) → it's read in the background (the blanks
 * found, and what each one shows) → review it here: the placement preview, the
 * sheet filled with sample values, the text version, and every blank's fact,
 * which can be changed → Activate. The office's Copy Image, Print PDF and Copy
 * Text then use it; Deactivate goes back to the standard exports. Each upload
 * or re-read is a new version, and a draft never touches the live one.
 */
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
const MAX_MB = 15;
const POLL_MS = 4000;
const BUSY = ["queued", "processing"];

export function checkUploadFile(file) {
  const name = String((file && file.name) || "").toLowerCase();
  const type = String((file && file.type) || "").toLowerCase();
  if (/\.(heic|heif)$/.test(name) || /heic|heif/.test(type)) {
    return "iPhone photos (HEIC) can't be read — save the photo as JPG or PDF and upload that.";
  }
  if (!/\.(pdf|png|jpe?g|webp)$/.test(name) && !/^(application\/pdf|image\/(png|jpe?g|webp))$/.test(type)) {
    return "Upload the sheet as a PDF, PNG, JPG or WEBP.";
  }
  if (file.size > MAX_MB * 1024 * 1024) return `The file is larger than ${MAX_MB} MB.`;
  return null;
}

function statusTag(v) {
  if (v.is_active) return <Tag color="green">Live</Tag>;
  if (v.status === "ready") return <Tag color="blue">Ready for review</Tag>;
  if (BUSY.indexOf(v.status) >= 0) return <Tag color="gold">Reading the sheet…</Tag>;
  return <Tag color="red">Failed</Tag>;
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** A canvas the size of what `paint(ctx)` draws, shown at the panel's width. */
function SheetCanvas({ width, height, paint, label }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas && canvas.getContext ? canvas.getContext("2d") : null;
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    paint(ctx);
  }, [width, height, paint]);
  return <canvas ref={ref} width={width} height={height} className="bf-canvas" aria-label={label} />;
}

function ReportAlerts({ fmt }) {
  const r = fmt.report || {};
  const list = (items) => items.map((i) => `${i.label || i.id} (${i.id})`).join(", ");
  const alerts = [];
  (r.page_notes || []).forEach((n, i) => alerts.push(<Alert key={`n${i}`} type="warning" showIcon message={n} />));
  if ((r.warnings || []).length) {
    alerts.push(
      <Alert key="w" type="warning" showIcon message="Corrected while reading — check these"
        description={<ul className="bf-list">{r.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>} />,
    );
  }
  if ((r.unbound || []).length) {
    alerts.push(<Alert key="u" type="warning" showIcon message={`Left empty: ${list(r.unbound)}`}
      description="Nothing we know fits these blanks. Choose a fact below if one does." />);
  }
  if ((r.low_confidence || []).length) {
    alerts.push(<Alert key="l" type="info" showIcon message={`Worth a look: ${list(r.low_confidence)}`} />);
  }
  if ((r.not_collected || []).length) {
    alerts.push(<Alert key="c" type="info" showIcon
      message={`Always empty — we don't collect: ${r.not_collected.map((n) => `${n.fact_label} (${n.id})`).join(", ")}`} />);
  }
  const unasked = (r.code_lookups || []).filter((c) => c.not_looked_up_by && c.not_looked_up_by.length);
  if (unasked.length) {
    alerts.push(
      <Alert key="k" type="info" showIcon message="Codes some of this office's carriers don't look up"
        description={(
          <ul className="bf-list">
            {unasked.map((c) => (
              <li key={c.code}>
                <strong>{c.code}</strong> — not looked up by {c.not_looked_up_by.join(", ")}.{" "}
                {c.line ? `Filled from the ${c.line} line when the carrier answers that.` : "Its blank stays empty for those patients."}
              </li>
            ))}
          </ul>
        )} />,
    );
  }
  if ((r.missed_blanks || []).length) {
    alerts.push(<Alert key="m" type="info" showIcon
      message={`Seen but not filled (no line to write on): ${r.missed_blanks.map((m) => `"${m.near_text}"`).join(", ")}`} />);
  }
  return alerts.length ? <Space direction="vertical" style={{ width: "100%" }}>{alerts}</Space> : null;
}

function FieldTable({ fmt, edits, errors, onEdit, onHover }) {
  const template = fmt.template;
  const options = useMemo(() => factOptions(template), [template]);
  const groupOf = useMemo(() => {
    const out = {};
    optionGroups(template.fields).forEach((g) => g.forEach((f) => { out[f.id] = g; }));
    return out;
  }, [template]);
  const current = (f) => ({ ...f, ...(edits[f.id] || {}) });

  const setFact = (f, fact) => {
    const targets = f.render === "mark" ? groupOf[f.id] || [f] : [f];
    targets.forEach((t) => {
      const change = { fact: fact || null };
      if (t.render !== "mark") {
        const allowed = formatsFor(fact);
        const fmtNow = current(t).format;
        if (allowed.indexOf(fmtNow) < 0) change.format = allowed[0];
      }
      onEdit(t.id, change);
    });
  };

  const columns = [
    {
      title: "Blank", key: "blank", width: 230,
      render: (_, f) => (
        <div>
          <div className="bf-label">
            <span className="bf-dot" style={{ background: STATE_COLOURS[blankState(current(f))] }} />
            {f.printed_label || f.question || f.id}
            {f.option_label ? <span className="bf-option"> [{f.option_label}]</span> : null}
          </div>
          <div className="bf-sub">
            {f.id}
            {f.note ? <Tooltip title={f.note}><span className="bf-note"> · reader's note</span></Tooltip> : null}
          </div>
          {(errors[f.id] || []).map((e, i) => <div key={i} className="bf-error">{e}</div>)}
        </div>
      ),
    },
    {
      title: "Shows", key: "fact",
      render: (_, f) => {
        const c = current(f);
        return (
          <div>
            <Select
              size="small"
              showSearch
              allowClear
              placeholder="Nothing (left empty)"
              value={c.fact || undefined}
              options={options}
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              style={{ width: "100%" }}
              onChange={(v) => setFact(f, v)}
              aria-label={`What ${f.id} shows`}
            />
            {c.fact && !factCollected(c.fact) ? <div className="bf-sub">Not collected yet — stays empty.</div> : null}
            {(c.fallbacks || []).length ? (
              <div className="bf-sub">If unknown: {c.fallbacks.map(factLabel).join(", then ")}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "How", key: "how", width: 150,
      render: (_, f) => {
        const c = current(f);
        if (!c.fact) return null;
        if (f.render === "mark") {
          const values = choiceValues(c.fact);
          return values ? (
            <Select size="small" value={c.mark_value} style={{ width: "100%" }} aria-label={`${f.id} ticked when`}
              options={values.map((v) => ({ value: v, label: `✓ when ${v}` }))}
              onChange={(v) => onEdit(f.id, { mark_value: v })} />
          ) : (
            <Input size="small" value={c.mark_value} prefix="✓ when" aria-label={`${f.id} ticked when`}
              onChange={(e) => onEdit(f.id, { mark_value: e.target.value })} />
          );
        }
        return (
          <Select size="small" value={c.format} style={{ width: "100%" }} aria-label={`${f.id} written as`}
            options={formatsFor(c.fact).map((v) => ({ value: v, label: v.replace("_", "/") }))}
            onChange={(v) => onEdit(f.id, { format: v })} />
        );
      },
    },
  ];

  return (
    <Table
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={template.fields}
      pagination={false}
      scroll={{ y: 460 }}
      rowClassName={(f) => (edits[f.id] ? "bf-row-edited" : "")}
      onRow={(f) => ({ onMouseEnter: () => onHover(f.id), onMouseLeave: () => onHover(null) })}
    />
  );
}

function Review({ fmt, officeName, onChanged }) {
  const [images, setImages] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [view, setView] = useState("placement");
  const [hover, setHover] = useState(null);
  const [edits, setEdits] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenNotes, setRegenNotes] = useState(fmt.instructions || "");
  const ready = fmt.status === "ready" && !!fmt.template;

  useEffect(() => {
    setEdits({});
    setErrors({});
    setImages(null);
    setImageError(false);
    if (!ready) return undefined;
    let alive = true;
    Promise.all((fmt.pages || []).map((p) => fetchProtectedImage(p.url)))
      .then((imgs) => { if (alive) setImages(imgs); })
      .catch(() => { if (alive) setImageError(true); });
    return () => { alive = false; };
  }, [fmt.id, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    if (!ready) return null;
    return { ...fmt.template, fields: fmt.template.fields.map((f) => ({ ...f, ...(edits[f.id] || {}) })) };
  }, [fmt, edits, ready]);
  const samples = useMemo(() => (shown ? sampleValues(shown) : {}), [shown]);
  const filled = useMemo(() => {
    if (!shown || !images || view !== "sample") return null;
    try {
      return paintPages(shown, samples, images);
    } catch (e) {
      return null;
    }
  }, [shown, samples, images, view]);

  const edit = useCallback((id, change) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...change } }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const save = async () => {
    setSaving(true);
    const r = await api.updateBreakdownFormat(fmt.id, { fields: edits });
    setSaving(false);
    if (r.status === 200 && r.json) {
      message.success("Saved.");
      setEdits({});
      onChanged(r.json);
    } else {
      setErrors((r.json && r.json.fields) || {});
      message.error((r.json && r.json.detail) || "Couldn't save the changes.");
    }
  };

  const act = async (name, call, success) => {
    setActing(name);
    const r = await call();
    setActing(null);
    if (r.status >= 200 && r.status < 300) {
      if (success) message.success(success);
      onChanged(r.json || null, name);
    } else {
      message.error((r.json && r.json.detail) || "That didn't work. Try again.");
    }
  };

  const viewOriginal = async () => {
    const popup = window.open("", "_blank");
    const r = await api.getBreakdownFormatSource(fmt.id);
    if (!r.blob) {
      if (popup) popup.close();
      message.error("Couldn't open the original.");
      return;
    }
    const url = URL.createObjectURL(r.blob);
    if (popup) popup.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
  };

  const editCount = Object.keys(edits).length;
  const counts = fmt.counts || {};

  const actions = (
    <Space wrap>
      {ready && !fmt.is_active ? (
        <Popconfirm
          title={`Use v${fmt.version} for ${officeName || "this office"}'s exports?`}
          description="Their Copy Image, Print PDF and Copy Text change right away."
          okText="Activate"
          onConfirm={() => act("activate", () => api.activateBreakdownFormat(fmt.id), `v${fmt.version} is live.`)}
          disabled={editCount > 0}
        >
          <Button type="primary" loading={acting === "activate"} disabled={editCount > 0}
            title={editCount ? "Save or discard your changes first" : undefined}>
            Activate
          </Button>
        </Popconfirm>
      ) : null}
      {fmt.is_active ? (
        <Popconfirm title="Go back to the standard exports for this office?" okText="Deactivate"
          onConfirm={() => act("deactivate", () => api.deactivateBreakdownFormat(fmt.id), "Back to the standard exports.")}>
          <Button loading={acting === "deactivate"}>Deactivate</Button>
        </Popconfirm>
      ) : null}
      <Button icon={<ReloadOutlined />} onClick={() => setRegenOpen(true)}>Read again…</Button>
      <Button icon={<FileSearchOutlined />} onClick={viewOriginal}>View original</Button>
      {!fmt.is_active ? (
        <Popconfirm title={`Delete v${fmt.version}?`} okText="Delete" okButtonProps={{ danger: true }}
          onConfirm={() => act("delete", () => api.deleteBreakdownFormat(fmt.id), `v${fmt.version} deleted.`)}>
          <Button danger loading={acting === "delete"} disabled={BUSY.indexOf(fmt.status) >= 0}>Delete</Button>
        </Popconfirm>
      ) : null}
    </Space>
  );

  const regenModal = (
    <Modal
      open={regenOpen}
      title="Read the sheet again"
      okText="Read again"
      confirmLoading={acting === "regenerate"}
      onCancel={() => setRegenOpen(false)}
      onOk={async () => {
        await act("regenerate", () => api.regenerateBreakdownFormat(fmt.id, regenNotes), "Reading the sheet again as a new draft.");
        setRegenOpen(false);
      }}
    >
      <p className="crm-sub">
        Makes a new draft from the same upload. {fmt.is_active ? "This live version stays live until you activate the new one." : ""}
      </p>
      <Input.TextArea
        rows={4}
        value={regenNotes}
        maxLength={2000}
        onChange={(e) => setRegenNotes(e.target.value)}
        placeholder={'Notes for the reader, e.g. "Root Planning is root planing, not scaling"'}
        aria-label="Notes for the reader"
      />
    </Modal>
  );

  if (BUSY.indexOf(fmt.status) >= 0) {
    return (
      <div className="bf-review">
        <Spin tip="Reading the sheet… usually a minute or two."><div style={{ height: 80 }} /></Spin>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="bf-review">
        <Alert type="error" showIcon message="This sheet couldn't be read" description={fmt.error || "Try reading it again."} />
        <div className="bf-actions">{actions}</div>
        {regenModal}
      </div>
    );
  }

  const pages = shown.pages || [];
  return (
    <div className="bf-review">
      <div className="bf-summary">
        <strong>{fmt.title || "Breakdown sheet"}</strong> · v{fmt.version} · {counts.blanks} blanks · {counts.bound} filled
        from each breakdown{counts.unbound ? ` · ${counts.unbound} left empty` : ""}
        {counts.not_collected ? ` · ${counts.not_collected} we don't collect` : ""}
      </div>
      <div className="bf-actions">{actions}</div>
      <ReportAlerts fmt={fmt} />

      <div className="bf-view-bar">
        <Segmented
          value={view}
          onChange={setView}
          options={[{ value: "placement", label: "What goes where" }, { value: "sample", label: "Filled with sample values" }]}
        />
        <span className="bf-legend">
          {[["bound", "filled"], ["unsure", "worth a look"], ["not_collected", "not collected"], ["unbound", "left empty"]]
            .map(([k, l]) => <span key={k}><span className="bf-dot" style={{ background: STATE_COLOURS[k] }} />{l}</span>)}
        </span>
      </div>
      <div className="bf-columns">
        <div className="bf-sheet">
          {imageError ? <Alert type="error" showIcon message="Couldn't load the sheet's pages." /> : null}
          {!images && !imageError ? <Spin /> : null}
          {images && view === "placement" ? pages.map((p, i) => (
            <SheetCanvas
              key={`p${i}`}
              width={p.width}
              height={p.height}
              label={`Page ${i + 1}: what goes where`}
              paint={(ctx) => drawPlacement(ctx, shown, i, images[i], { hover })}
            />
          )) : null}
          {images && view === "sample" && filled ? filled.map((c, i) => (
            <SheetCanvas key={`s${i}`} width={c.width} height={c.height} label={`Page ${i + 1} with sample values`}
              paint={(ctx) => ctx.drawImage(c, 0, 0)} />
          )) : null}
        </div>
        <div className="bf-fields">
          <div className="bf-fields-bar">
            <span className="crm-sub">Hover a blank to find it on the sheet.</span>
            {editCount ? (
              <Space>
                <Button size="small" onClick={() => { setEdits({}); setErrors({}); }}>Discard</Button>
                <Button size="small" type="primary" loading={saving} onClick={save}>
                  Save {editCount} change{editCount === 1 ? "" : "s"}
                </Button>
              </Space>
            ) : null}
          </div>
          {fmt.is_active && editCount ? (
            <Alert type="warning" showIcon style={{ marginBottom: 8 }}
              message="This version is live: saved changes reach the office's exports right away." />
          ) : null}
          <FieldTable fmt={{ ...fmt, template: fmt.template }} edits={edits} errors={errors} onEdit={edit} onHover={setHover} />
        </div>
      </div>
      <Collapse
        style={{ marginTop: 12 }}
        items={[{
          key: "text",
          label: "Copy Text version (sample values)",
          children: <pre className="bf-text">{renderFormatText(shown, samples, { provenance: { date: "today", sourceLabel: "Sample" } })}</pre>,
        }]}
      />
      {regenModal}
    </div>
  );
}

export default function OfficeBreakdownFormatPanel({ officeId, officeName }) {
  const [versions, setVersions] = useState(null);
  const [failed, setFailed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState("");
  const poll = useRef(null);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    const r = await api.getBreakdownFormat(id);
    if (r.status === 200 && r.json) setDetail(r.json);
  }, []);

  const loadList = useCallback(async (preferId) => {
    const r = await api.getBreakdownFormats(officeId);
    const ok = r.status === 200 && r.json && Array.isArray(r.json.results);
    setFailed(!ok);
    if (!ok) return null;
    const list = r.json.results;
    setVersions(list);
    setSelectedId((prev) => {
      const keep = preferId || prev;
      if (keep && list.some((v) => v.id === keep)) return keep;
      const live = list.find((v) => v.is_active);
      return live ? live.id : (list[0] ? list[0].id : null);
    });
    return list;
  }, [officeId]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // While a sheet is being read, check back every few seconds.
  useEffect(() => {
    const busy = (versions || []).some((v) => BUSY.indexOf(v.status) >= 0);
    if (!busy) return undefined;
    poll.current = setTimeout(async () => {
      const list = await loadList();
      const mine = list && list.find((v) => v.id === selectedId);
      if (mine && BUSY.indexOf(mine.status) < 0) loadDetail(selectedId);
    }, POLL_MS);
    return () => clearTimeout(poll.current);
  }, [versions, selectedId, loadList, loadDetail]);

  const upload = async (file) => {
    const problem = checkUploadFile(file);
    if (problem) {
      message.error(problem);
      return;
    }
    setUploading(true);
    const r = await api.uploadBreakdownFormat(officeId, file, notes.trim());
    setUploading(false);
    if (r.status === 201 && r.json) {
      message.success("Uploaded — reading the sheet.");
      setNotes("");
      await loadList(r.json.id);
      setDetail(r.json);
    } else {
      message.error((r.json && r.json.detail) || "The upload didn't work. Try again.");
    }
  };

  const changed = async (json, action) => {
    if (action === "delete") {
      setDetail(null);
      setSelectedId(null);
      await loadList();
      return;
    }
    if (action === "regenerate" && json) {
      await loadList(json.id);
      setDetail(json);
      return;
    }
    if (json) setDetail(json);
    await loadList(json ? json.id : null);
  };

  if (failed) {
    return (
      <p className="crm-sub">
        Couldn&rsquo;t load this office&rsquo;s breakdown formats.{" "}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => loadList()}>Try again</Button>
      </p>
    );
  }
  if (!versions) return <Spin />;

  const live = versions.find((v) => v.is_active);
  return (
    <div className="bf-panel">
      <p className="crm-sub">
        Upload a copy of this office&rsquo;s own breakdown sheet. It&rsquo;s read for you: every blank is found and
        matched to a breakdown detail. Check it below, then Activate: the office&rsquo;s Copy Image and Print PDF
        become their sheet filled in, and Copy Text follows its order.{" "}
        {live ? <strong>Live: v{live.version}.</strong> : <strong>No format is live — the office uses the standard exports.</strong>}
      </p>
      <div className="bf-upload">
        <Upload.Dragger
          multiple={false}
          showUploadList={false}
          accept={ACCEPT}
          disabled={uploading}
          beforeUpload={(file) => { upload(file); return false; }}
        >
          <p className="bf-upload-icon"><CloudUploadOutlined /></p>
          <p>{uploading ? "Uploading…" : "Drop the office's breakdown sheet here, or browse"}</p>
          <p className="crm-sub">A photo, scan or PDF — up to {MAX_MB} MB, one or two pages. A blank sheet reads best.</p>
        </Upload.Dragger>
        <Input.TextArea
          rows={2}
          value={notes}
          maxLength={2000}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes for the reader (optional)"
          aria-label="Notes for the reader (optional)"
        />
      </div>

      {versions.length ? (
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={versions}
          rowClassName={(v) => (v.id === selectedId ? "bf-row-selected" : "")}
          onRow={(v) => ({ onClick: () => setSelectedId(v.id), style: { cursor: "pointer" } })}
          columns={[
            { title: "Version", key: "v", width: 80, render: (_, v) => `v${v.version}` },
            { title: "Status", key: "s", width: 150, render: (_, v) => statusTag(v) },
            {
              title: "Blanks", key: "b", width: 120,
              render: (_, v) => (v.counts && v.counts.blanks != null ? `${v.counts.bound} of ${v.counts.blanks} filled` : ""),
            },
            { title: "File", key: "f", render: (_, v) => v.source_name },
            {
              title: "Uploaded", key: "u", width: 220,
              render: (_, v) => `${when(v.created_at)}${v.created_by ? ` · ${v.created_by.name}` : ""}`,
            },
          ]}
        />
      ) : <Empty description="No breakdown sheet uploaded yet." />}

      {detail && detail.id === selectedId ? (
        <Review key={detail.id} fmt={detail} officeName={officeName} onChanged={changed} />
      ) : selectedId ? <Spin /> : null}
    </div>
  );
}
